import {useMutation} from "@apollo/client";
import {yupResolver} from "@hookform/resolvers/yup";
import {useEffect} from "react";
import {FormProvider, useForm} from "react-hook-form";
import toast from "react-hot-toast";
import * as Yup from "yup";

import Button from "@/components/button/Button.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {SelectField} from "@/components/form/fields/SelectField/SelectField.tsx";
import {TimezoneSelectField} from "@/components/form/fields/TimezoneSelectField/TimezoneSelectField.tsx";
import {DataRegions} from "@/lib/constants/account";
import {GQL_ACCOUNTDETAILS_UPDATE, UpdateAccountDetailsResult} from "@/lib/queries/accounts";
import {Account} from "@/lib/types/accounts";
import {ResponseStatus} from "@/lib/types/common";

interface SubaccountDetailsFormValues {
  name?: string;
  timezone?: string;
  data_region: string;
}

type SubaccountDetailsFormKeys = keyof SubaccountDetailsFormValues;

const FORM_FIELDS: SubaccountDetailsFormKeys[] = ["name", "timezone", "data_region"];
const GLOBALLY_TOASTED_KEY = "__all__";

const EditAccountDetailsSchema = Yup.object({
  name: Yup.string().optional(),
  timezone: Yup.string().optional(),
  data_region: Yup.string().required("This field cannot be blank."),
});

type SubaccountDetailsFormProps = {
  accountDetails: Account;
};

export const SubaccountDetailsForm = ({accountDetails}: SubaccountDetailsFormProps) => {
  const [updateAccountDetails, {loading}] = useMutation<UpdateAccountDetailsResult>(GQL_ACCOUNTDETAILS_UPDATE);

  const methods = useForm<SubaccountDetailsFormValues>({
    resolver: yupResolver<SubaccountDetailsFormValues>(EditAccountDetailsSchema),
    defaultValues: {
      name: accountDetails?.name,
      timezone: accountDetails?.timezone,
      data_region: accountDetails?.data_region,
    },
  });

  useEffect(() => {
    methods.reset({
      name: accountDetails?.name,
      timezone: accountDetails?.timezone,
      data_region: accountDetails?.data_region,
    });
  }, [accountDetails, methods]);

  const onSubmit = async (data: SubaccountDetailsFormValues) => {
    let resp;
    try {
      resp = await updateAccountDetails({
        variables: {data},
      });
    } catch {
      toast.error("Could not update account details. Please try again.");
      return;
    }

    if (resp.data?.response?.status === ResponseStatus.SUCCESS) {
      toast.success("Account details updated successfully.");
      return;
    }

    const messages = Object.entries(resp.data?.response?.message ?? {});
    const withoutAField: string[] = [];
    messages.forEach(([fieldName, messageList]) => {
      if (FORM_FIELDS.includes(fieldName as SubaccountDetailsFormKeys)) {
        methods.setError(fieldName as SubaccountDetailsFormKeys, {message: messageList[0]});
      } else if (fieldName !== GLOBALLY_TOASTED_KEY) {
        // This form renders no input for the field, so setError would leave the error invisible.
        withoutAField.push(messageList[0]);
      }
    });

    if (withoutAField.length > 0) {
      toast.error(withoutAField.join(" "));
    } else if (messages.length === 0) {
      toast.error("Could not update account details. Please try again.");
    }
  };

  const dataRegionOptions = Object.entries(DataRegions).map(([value, label]) => ({
    label,
    value,
  }));

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          methods.handleSubmit(onSubmit)(e);
        }}
        noValidate
      >
        <div className="col-xl-7">
          <div className="row">
            <div className="col-lg-6">
              <InputField name="name" label="Subaccount" />
            </div>
          </div>
          <div className="row mt-3">
            <div className="col-lg-6">
              <TimezoneSelectField name={"timezone"} />
            </div>
            <div className="col-lg-6 mt-3 mt-lg-0">
              <SelectField
                name="data_region"
                label="Data Region"
                options={dataRegionOptions}
                isClearable={false}
                tooltip="This option configures the location where RUM (Real Time User Monitoring) data is stored. This setting is per account. If required, multiple sub-accounts can be created with different data regions enabling data compliance and data locality separation. Note: Please get in touch with our support team for assistance if you have existing RUM checks and would like to change this setting."
              />
            </div>
          </div>
          <Button type="submit" className="mt-3 float-end" disabled={loading}>
            {loading ? "Saving Details..." : "Update Details"}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default SubaccountDetailsForm;
